/**
 * Minimal Button component — shadcn/ui-compatible API, hand-written to avoid CLI deps.
 * Variants: default (primary blue), outline, ghost, destructive, outlineDestructive,
 * outlinePrimary.
 *
 * A variant owns background AND text colour. Overriding either from className is a coin
 * flip: two utilities on one property resolve by stylesheet order, not by the order they
 * appear in className. A red delete button built that way rendered a red border with a
 * slate icon. If a look is not here, add it here rather than patching a call site.
 *
 * Sizes: md (default), sm, xs. The middle size exists because the app kept hand-rolling it:
 * a survey found 74 buttons across 48 files carrying 54 different class combinations, and the
 * most common shape by far was px-3 py-1.5 - between the two sizes this component used to offer.
 * People were not being careless; the size they needed was missing.
 */
import type { ButtonHTMLAttributes } from 'react'

export type ButtonVariant =
  | 'default'
  | 'outline'
  | 'ghost'
  | 'destructive'
  | 'outlineDestructive'
  | 'outlinePrimary'
  | 'warning'
type ButtonSize = 'xs' | 'sm' | 'md'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
}

const VARIANTS: Record<ButtonVariant, string> = {
  default: 'bg-primary text-white hover:bg-primary-dark active:bg-primary-dark',
  outline: 'border border-border bg-surface text-fg-soft hover:bg-app',
  ghost: 'text-fg-soft hover:bg-hover active:bg-hover',
  destructive: 'bg-destructive text-white hover:opacity-90 active:opacity-90',
  // A destructive action that is not the page's main one — a delete icon in a table row. Solid
  // red would shout over every row; a red outline says the same thing at row weight.
  outlineDestructive:
    'border border-destructive bg-surface text-destructive hover:bg-destructive/10',
  // An emphasised secondary action: reads as available without competing with the primary button.
  outlinePrimary: 'border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20',
  // A "do it anyway past the guardrail" action — confirming a change the ceiling refused. Amber says
  // proceed-with-care without the finality of a red destructive button.
  warning: 'bg-amber-600 text-white hover:bg-amber-700 active:bg-amber-700',
}

const SIZES: Record<ButtonSize, string> = {
  md: 'px-4 py-2 text-sm',
  sm: 'px-3 py-1.5 text-xs',
  xs: 'px-2.5 py-1 text-xs',
}

export function Button({
  className = '',
  variant = 'default',
  size = 'md',
  children,
  ...props
}: ButtonProps) {
  const base =
    'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50'

  return (
    <button className={`${base} ${SIZES[size]} ${VARIANTS[variant]} ${className}`} {...props}>
      {children}
    </button>
  )
}
